import { useHistory, useLocation } from 'react-router-dom'
import qs from 'qs'

interface IPreserveSearch {
  pathname: string
  search?: string
}

/**
 *
 * @param entries tuple, example: [ ['foo', '1' ], ['boo', '2' ] ]
 * @return a object based on entries tuple
 * @example { foo: '1', boo: '2' } or {}
 */
function paramsToObject(entries: IterableIterator<[string, string]>): object {
  const entriesArr = Array.from(entries)

  return entriesArr.reduce((prev: any, current: any) => {
    const [key, value] = current
    let newValue = value
    if (prev[key]) {
      newValue = Array.isArray(prev[key])
        ? [...prev[key], value]
        : [prev[key], value]
    }
    return { ...prev, [key]: newValue }
  }, {})
}

function convertStringParamsToObject(path: string | undefined): object {
  if (!path) {
    return {}
  }

  const searchUseLocationUrlParams = new URLSearchParams(path)
  const entries = searchUseLocationUrlParams.entries() //returns an iterator of decoded [key,value] tuples
  return paramsToObject(entries)
}

export type UseRouterReturnType = ReturnType<typeof useHistory> & {
  pushPreserveSearch: (value: string | IPreserveSearch) => void
  pushPreserveState: (pathname: string) => void
}

export const useRouter = (): UseRouterReturnType => {
  const router = useHistory()
  const { search: searchOfURL, state } = useLocation()
  const searchOfUrlObj = convertStringParamsToObject(searchOfURL)

  /**
   * preserve search (query params) of URL
   * @example /path?foo=boo
   */
  const pushPreserveSearch = (value: string | IPreserveSearch) => {
    if (typeof value !== 'object') {
      const pathname = value.split('?')[0]
      const pathnameSearch = value.split('?')[1]
      const pathnameSearchObj = convertStringParamsToObject(pathnameSearch)
      const allSearchParamsMixed = {
        ...searchOfUrlObj,
        ...pathnameSearchObj,
      }
      const searchString = qs.stringify(allSearchParamsMixed, {
        arrayFormat: 'repeat',
      })
      const search = searchString ? `?${searchString}` : ''

      router.push({
        pathname,
        search,
      })
      return
    }

    const pathname = value.pathname.split('?')[0]
    const pathnameSearch = value.pathname.split('?')[1]
    let search

    if (pathnameSearch || (searchOfURL && value.search)) {
      const searchOfPathNameObj = convertStringParamsToObject(pathnameSearch)
      const searchOfValueObj = convertStringParamsToObject(value.search)
      const mixAllSearchParams = {
        ...searchOfUrlObj,
        ...searchOfPathNameObj,
        ...searchOfValueObj,
      }
      const searchString = qs.stringify(mixAllSearchParams, {
        arrayFormat: 'repeat',
      })

      search = `?${searchString}`
    } else if (searchOfURL) {
      search = searchOfURL
    } else {
      search = value.search
    }

    router.push({
      pathname,
      search,
    })
  }

  const pushPreserveState = (pathname: string) => {
    router.push({
      pathname: pathname,
      search: searchOfURL,
      state: state,
    })
  }

  return { ...router, pushPreserveSearch, pushPreserveState }
}
